import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import "@/App.css";
import { 
  LayoutDashboard, 
  Rocket, 
  Users, 
  Mail, 
  Calendar, 
  Settings,
  Zap,
  Sun,
  Moon,
  LogOut,
  Globe,
  ShoppingCart,
  History,
  Search
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Pages
import Dashboard from "@/pages/Dashboard";
import CampaignSetup from "@/pages/CampaignSetup";
import LeadsList from "@/pages/LeadsList";
import LeadDetail from "@/pages/LeadDetail";
import EmailOutbox from "@/pages/EmailOutbox";
import Scheduling from "@/pages/Scheduling";
import BookingPage from "@/pages/BookingPage";
import SettingsPage from "@/pages/SettingsPage";
import PreviewPage from "@/pages/PreviewPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SingleBuildPage from "@/pages/SingleBuildPage";
import PortalOrdersPage from "@/pages/PortalOrdersPage";
import BuildsPage from "@/pages/BuildsPage";
import ScraperPage from "@/pages/ScraperPage";

// Portal Pages (Public)
import PortalLanding from "@/pages/portal/PortalLanding";
import PortalVerify from "@/pages/portal/PortalVerify";
import PortalBuild from "@/pages/portal/PortalBuild";
import PortalPreview from "@/pages/portal/PortalPreview";
import PortalCheckout from "@/pages/portal/PortalCheckout";
import PortalDownload from "@/pages/portal/PortalDownload";
import PortalUnsubscribe from "@/pages/portal/PortalUnsubscribe";

// Admin base path - all admin routes will be under /admin
const ADMIN_BASE = "/admin";

// Protected Route wrapper for admin
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to admin login, preserving the intended destination
    return <Navigate to={`${ADMIN_BASE}/login`} state={{ from: location }} replace />;
  }

  return children;
};

const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();
  
  return (
    <Button 
      variant="ghost" 
      size="icon"
      onClick={toggleTheme}
      className="theme-toggle"
      data-testid="theme-toggle"
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      {theme === 'light' ? (
        <Moon size={18} className="text-muted-foreground" />
      ) : (
        <Sun size={18} className="text-yellow-400" />
      )}
    </Button>
  );
};

const LogoutButton = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    await logout();
    navigate(`${ADMIN_BASE}/login`);
  };
  
  return (
    <div className="logout-section">
      <span className="user-info">{user?.username}</span>
      <Button 
        variant="ghost" 
        size="icon"
        onClick={handleLogout}
        className="logout-btn"
        data-testid="logout-btn"
        aria-label="Logout"
        title="Logout"
      >
        <LogOut size={18} />
      </Button>
    </div>
  );
};

const Sidebar = () => {
  const location = useLocation();
  
  // All nav items are relative to admin base
  const navItems = [
    { path: `${ADMIN_BASE}`, icon: LayoutDashboard, label: "Dashboard", exact: true },
    { path: `${ADMIN_BASE}/campaigns/new`, icon: Rocket, label: "New Campaign" },
    { path: `${ADMIN_BASE}/single-build`, icon: Globe, label: "Single Site Build" },
    { path: `${ADMIN_BASE}/leads`, icon: Users, label: "Leads" },
    { path: `${ADMIN_BASE}/portal-orders`, icon: ShoppingCart, label: "Portal Orders" },
    { path: `${ADMIN_BASE}/emails`, icon: Mail, label: "Email Outbox" },
    { path: `${ADMIN_BASE}/scheduling`, icon: Calendar, label: "Scheduling" },
    { path: `${ADMIN_BASE}/builds`, icon: History, label: "Build History" },
    { path: `${ADMIN_BASE}/scraper`, icon: Search, label: "Scraper" },
    { path: `${ADMIN_BASE}/settings`, icon: Settings, label: "Settings" },
  ];

  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <div className="sidebar-logo-icon">
            <Zap size={20} />
          </div>
          <span className="sidebar-logo-text">LeadForge</span>
        </div>
      </div>
      <nav className="sidebar-nav">
        {navItems.map((item) => {
          const isActive = item.exact 
            ? location.pathname === item.path
            : location.pathname.startsWith(item.path);
          
          return (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`sidebar-link ${isActive ? 'sidebar-link-active' : ''}`}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="sidebar-footer">
        <ThemeToggle />
        <LogoutButton />
      </div>
    </aside>
  );
};

const AppLayout = ({ children }) => {
  return (
    <div className="app-container">
      <Sidebar />
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <>
      <BrowserRouter>
        <Routes>
          {/* Public preview and booking routes */}
          <Route path="/preview/:leadId" element={<PreviewPage />} />
          <Route path="/book" element={<BookingPage />} />
          
          {/* Customer Portal Routes (PUBLIC - no admin auth) */}
          <Route path="/" element={<PortalLanding />} />
          <Route path="/portal/start" element={<PortalLanding />} />
          <Route path="/portal/verify" element={<PortalVerify />} />
          <Route path="/portal/build" element={<PortalBuild />} />
          <Route path="/portal/preview/:leadId" element={<PortalPreview />} />
          <Route path="/portal/checkout/:leadId" element={<PortalCheckout />} />
          <Route path="/portal/download/:leadId" element={<PortalDownload />} />
          <Route path="/portal/unsubscribe" element={<PortalUnsubscribe />} />
          
          {/* Admin login route */}
          <Route 
            path={`${ADMIN_BASE}/login`}
            element={
              isAuthenticated ? <Navigate to={ADMIN_BASE} replace /> : <LoginPage />
            } 
          />
          
          {/* Admin password reset routes (public) */}
          <Route path={`${ADMIN_BASE}/forgot-password`} element={<ForgotPasswordPage />} />
          <Route path={`${ADMIN_BASE}/reset-password`} element={<ResetPasswordPage />} />
          
          {/* Legacy login redirect */}
          <Route path="/login" element={<Navigate to={`${ADMIN_BASE}/login`} replace />} />
          
          {/* Admin routes - auth required */}
          <Route path={`${ADMIN_BASE}/*`} element={
            <ProtectedRoute>
              <AppLayout>
                <Routes>
                  <Route index element={<Dashboard />} />
                  <Route path="campaigns/new" element={<CampaignSetup />} />
                  <Route path="single-build" element={<SingleBuildPage />} />
                  <Route path="leads" element={<LeadsList />} />
                  <Route path="leads/:leadId" element={<LeadDetail />} />
                  <Route path="portal-orders" element={<PortalOrdersPage />} />
                  <Route path="emails" element={<EmailOutbox />} />
                  <Route path="scheduling" element={<Scheduling />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="builds" element={<BuildsPage />} />
                  <Route path="scraper" element={<ScraperPage />} />
                  {/* Catch-all for unknown admin routes - stay in admin */}
                  <Route path="*" element={<Navigate to={ADMIN_BASE} replace />} />
                </Routes>
              </AppLayout>
            </ProtectedRoute>
          } />
          
          {/* Catch-all: redirect unknown routes to portal landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
